import * as React from 'react';
import {
  Avatar,
  Button,
  CssBaseline,
  TextField,
  Link,
  Paper,
  Box,
  Typography,
  createTheme,
  ThemeProvider,
  Container
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Helmet } from "react-helmet";
import useApi from '../Utils/useApi';

const theme = createTheme({
  typography: {
    htmlFontSize: 20,
    h1: {
      fontSize: 'clamp(2rem, 5vw, 3rem)',
      fontWeight: 'bold',
    },
    h5: {
      fontSize: 'clamp(1rem, 2.5vw, 1.5rem)',
      fontWeight: 'bold',
    },
    body1: {
      fontSize: '1.2rem',
    },
  },
});

export default function ForgotPassword() {
  const [email, setEmail] = React.useState("");
  const [message, setMessage] = React.useState("");
  const api = useApi();

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      const response = await api.post('/user/request-password-reset', { email });
      if (response.data.msg) {
        setMessage(response.data.msg);
      } else {
        setMessage("If that email exists in our system, you'll receive a reset link shortly.");
      }
    } catch (error) {
      console.error(`Error requesting password reset:`, error);
      setMessage("An error occurred. Please try again.");
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Helmet>
        <title>Forgot Your Password? Reset It Here - Rocket Learning</title>
        <meta name="description" content="Forgot your password? Enter your email to get a password reset link and access your Rocket Learning account." />
      </Helmet>

      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #4a91e3 0%, #4a91e3 100%)',
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={6}
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 2,
            }}
          >
            <Avatar sx={{ m: 1, bgcolor: '#f2182c' }}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
              Forgot Password
            </Typography>

            {message ? (
              <Typography color="textSecondary" align="center">{message}</Typography>
            ) : (
              <>
                <Typography component="p" variant="body1" align="center" sx={{ mb: 2 }}>
                  Enter your email address below and we’ll send you a reset link. 
                  Don’t forget to check your spam folder if you don’t see it.
                </Typography>
                <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{
                      mt: 3,
                      mb: 2,
                      backgroundColor: '#ffcc00',
                      color: '#000',
                      fontWeight: 'bold',
                      textTransform: 'none',
                      '&:hover': { backgroundColor: '#ffb700' },
                    }}
                  >
                    Send Reset Link
                  </Button>
                </Box>
              </>
            )}

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link href="/" variant="body2" sx={{ display: 'block', mb: 1 }}>
                Return to Homepage
              </Link>
              <Link href="/ParentLogin" variant="body2">
                Remembered? Sign in
              </Link>
            </Box>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
